import { useState } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2 } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { startGoogleLogin } from "@/pages/landing/primitives";
import { postAuthDestination } from "@/lib/planSelection";
import AuthShell from "./AuthShell";
import GoogleButton from "./GoogleButton";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { checkAuth } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState("");

  const nextParam = new URLSearchParams(location.search).get("next");

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!email || !password) { setErr("Enter your email and password."); return; }
    setLoading(true);
    try {
      await api.post("/auth/login", { email: email.trim(), password });
      await checkAuth();
      toast.success("Welcome back!");
      navigate(nextParam || postAuthDestination() || "/dashboard");
    } catch (e) {
      const status = e?.response?.status;
      const detail = e?.response?.data?.detail || "Sign-in failed. Please try again.";
      setErr(detail);
      if (status === 403 && /verify/i.test(detail)) {
        // Give the user a one-click resend affordance.
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <AuthShell
      title="Welcome back"
      subtitle="Sign in to your Interest To Income account."
      testId="login-page"
      footer={
        <>New here? <Link to={`/signup${location.search}`} className="text-purple-700 font-semibold hover:underline" data-testid="login-goto-signup">Create an account</Link></>
      }
    >
      <GoogleButton label="Continue with Google" onClick={startGoogleLogin} testId="login-google-btn" />
      <div className="my-6 flex items-center gap-3 text-xs text-slate-400">
        <div className="h-px flex-1 bg-slate-200" />
        <span>OR</span>
        <div className="h-px flex-1 bg-slate-200" />
      </div>
      <form onSubmit={submit} className="space-y-4" data-testid="login-form">
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-1">Email</label>
          <input
            type="email"
            autoComplete="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            data-testid="login-email"
            className="w-full border-2 border-slate-200 focus:border-black rounded-xl px-4 py-3 outline-none transition"
            placeholder="you@example.com"
          />
        </div>
        <div>
          <div className="flex items-center justify-between mb-1">
            <label className="block text-sm font-semibold text-slate-700">Password</label>
            <Link to="/forgot-password" className="text-xs text-purple-700 font-semibold hover:underline" data-testid="login-forgot-link">
              Forgot password?
            </Link>
          </div>
          <div className="relative">
            <input
              type={showPw ? "text" : "password"}
              autoComplete="current-password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              data-testid="login-password"
              className="w-full border-2 border-slate-200 focus:border-black rounded-xl px-4 py-3 pr-11 outline-none transition"
              placeholder="Your password"
            />
            <button
              type="button"
              onClick={() => setShowPw((s) => !s)}
              data-testid="login-toggle-pw"
              className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-700"
              aria-label={showPw ? "Hide password" : "Show password"}
            >
              {showPw ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
            </button>
          </div>
        </div>
        {err && (
          <div data-testid="login-error" className="bg-red-50 border border-red-200 text-red-800 text-sm rounded-lg px-3 py-2">
            {err}
          </div>
        )}
        <button
          type="submit"
          disabled={loading}
          data-testid="login-submit"
          className="w-full inline-flex items-center justify-center gap-2 iti-btn-primary px-4 py-3 font-semibold disabled:opacity-60"
        >
          {loading ? <Loader2 className="w-5 h-5 animate-spin" /> : "Sign in"}
        </button>
      </form>
    </AuthShell>
  );
}
